import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Injectable, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlValueAccessor, FormArray, FormBuilder, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validator, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular/standalone';
import { enterLeave } from 'src/app/animations/common.animations';
import { ItemValueComponent } from 'src/app/components/item-value/item-value.component';
import { ItemComponent } from 'src/app/components/item/item.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { ComplicationNameSearchModal } from 'src/app/modals/complication-name-search/complication-name-search.modal';
import { SelectControl, SelectItem, SelectValue } from '../select/select.control';
import { TextAreaControl } from '../textarea/textarea.control';

export interface ComplicationValue {
	name: string;
	notes: string | null;
}

export interface ComplicationsPickerValue {
	category: string | null;
	subCategory: string | null;
	complications: ComplicationValue[];
}

type ComplicationValueFormGroup = FormGroup<{
	name: FormControl<string>;
	notes: FormControl<string | null>;
}>;

@Component({
	selector: 'app-complications-picker',
	templateUrl: './complications-picker.control.html',
	styleUrl: './complications-picker.control.scss',
	imports: [ItemComponent, ItemValueComponent, SvgIconComponent, ReactiveFormsModule, SelectControl, TextAreaControl],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: ComplicationsPickerControl,
			multi: true
		},
		{
			provide: NG_VALIDATORS,
			multi: true,
			useExisting: ComplicationsPickerControl
		}
	],
	animations: [enterLeave]
})
export class ComplicationsPickerControl implements ControlValueAccessor, Validator, OnInit {

	@Input() readOnly: boolean = false;

	categoryControl!: FormControl<string | null>;
	subCategoryControl!: FormControl<string | null>;
	complicationsArray!: FormArray<ComplicationValueFormGroup>;
	formGroup!: FormGroup;

	categoryItems: SelectItem[] = [];
	subCategoryItems: SelectItem[] = [];

	constructor(
		private changeDetectorRef: ChangeDetectorRef,
		private complicationCategoryService: ComplicationCategoryService,
		private formBuilder: FormBuilder,
		private destroyRef: DestroyRef, // note: DestroyRef is needed if takeUntilDestroyed is used outside the constructor
		private modalController: ModalController) {

		// initialise the category items (these never change)
		this.categoryItems = this.complicationCategoryService.getCategoryNames().map(categoryName => ({ text: categoryName }));

		// initialise the form
		this.categoryControl = this.formBuilder.control(null, { validators: [Validators.required] });
		this.subCategoryControl = this.formBuilder.control(null, { validators: [Validators.required] });
		this.complicationsArray = this.formBuilder.array<ComplicationValueFormGroup>([], { validators: [Validators.minLength(1)] });
		this.formGroup = this.formBuilder.group({
			category: this.categoryControl,
			subCategory: this.subCategoryControl,
			complications: this.complicationsArray
		});
	}

	ngOnInit(): void {
		if (!this.readOnly) {
			this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: ComplicationsPickerValue) => {
				this.onChange(value);
				this.onValidatorChange();
				this.changeDetectorRef.markForCheck();
			});
		}
	}

	writeValue(value: ComplicationsPickerValue | null): void {
		if (value) {
			if (value.category) {
				this.subCategoryItems = this.complicationCategoryService.getSubCategories(value.category).map(subCategory => ({ text: subCategory }));
			}
			else {
				this.subCategoryItems = [];
			}

			this.categoryControl.setValue(value.category, { emitEvent: false });
			this.subCategoryControl.setValue(value.subCategory, { emitEvent: false });
			this.complicationsArray.clear();
			value.complications.forEach(complication => {
				this.complicationsArray.push(this.formBuilder.group({
					name: this.formBuilder.control(complication.name),
					notes: this.formBuilder.control(complication.notes)
				}) as ComplicationValueFormGroup, { emitEvent: false });
			});
		} else {
			this.subCategoryItems = [];
			this.categoryControl.setValue(null, { emitEvent: false });
			this.subCategoryControl.setValue(null, { emitEvent: false });
			this.complicationsArray.clear({ emitEvent: false });
		}

		// this is required for the view to update correctly (it's a general approach recommended by Angular devs using ChangeDetectionStrategy.OnPush: https://github.com/angular/angular/issues/21780)
		this.changeDetectorRef.markForCheck();
	}

	private onChange: (value: ComplicationsPickerValue | null) => void = () => { };

	registerOnChange(fn: (value: ComplicationsPickerValue | null) => void): void {
		this.onChange = fn;
	}

	private onTouched: () => void = () => { };

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(disabled: boolean): void {
	}

	controlClicked() {
		if (this.readOnly) return;
		this.onTouched();
	}

	categoryChanged(category: SelectValue) {
		this.subCategoryItems = this.complicationCategoryService.getSubCategories((category as string)).map(subCategory => ({ text: subCategory }));
		this.subCategoryControl.setValue(null);
	}

	async addComplicationClicked() {
		this.controlClicked();

		const popover = await this.modalController.create({
			component: ComplicationNameSearchModal,
			cssClass: 'stacked-modal',
			backdropDismiss: false
		});

		await popover.present();

		const { data } = await popover.onWillDismiss();

		if (!data) return;

		const name: string = data.name;

		this.complicationsArray.push(this.formBuilder.group({
			name: this.formBuilder.control(name),
			notes: this.formBuilder.control(null)
		}) as ComplicationValueFormGroup);
	}

	getNotesControl(formGroup: AbstractControl) {
		return formGroup.get('notes') as FormControl;
	}

	removeComplicationClicked(index: number) {
		this.complicationsArray.removeAt(index);
	}

	// custom validation
	private onValidatorChange: () => void = () => { }; // call this when the internal state has changed requiring external validation

	registerOnValidatorChange(fn: () => void): void {
		this.onValidatorChange = fn;
	}

	validate(): ValidationErrors | null {
		const value = this.formGroup.value as ComplicationsPickerValue;
		if (!value.category) return { categoryRequired: true };
		if (!value.subCategory) return { subCategoryRequired: true };
		if (!value.complications.length) return { complicationsRequired: true };
		return null;
	}
}

// service to provide the complication categories and subcategories

export interface ComplicationCategory {
	categoryName: string;
	subCategories: string[];
}

@Injectable({
	providedIn: 'root'
})
export class ComplicationCategoryService {

	private categories: ComplicationCategory[] = [
		{
			categoryName: 'Anaesthesia & Sedation',
			subCategories: [
				'Anaesthesia complication',
				'Anaesthesia recovery complication',
				'Local anaesthesia complication',
				'Sedation complication'
			]
		},
		{
			categoryName: 'Catheters, tubes, and drains',
			subCategories: [
				'Arterial catheter complication',
				'Central venous catheter complication',
				'Feeding tube complication',
				'Jugular catheter complication',
				'Peripheral catheter complication',
				'Thoracostomy tube placement complication',
				'Urinary catheter complication',
				'Pericardial catheter complication',
				'Abdominal drain complication'
			]
		},
		{
			categoryName: 'Implanted Devices',
			subCategories: [
				'Bone implant complication',
				'Corneal graft complication',
				'Orthopaedic plate/screw complication',
				'SUB complication',
				'Tracheal stent complication',
				'Urethral stent complication',
				'Ureteral stent complication'
			]
		},
		{
			categoryName: 'Procedure Complications',
			subCategories: [
				'Cardiology interventional procedure complication',
				'Cardiothoracic surgical procedure complication',
				'Dermatology procedure complication',
				'Diagnostic imaging procedure complication',
				'Diagnostic procedure complication',
				'ECC procedure complication',
				'ECC treatment complication',
				'ECC ventilation complication',
				'Internal medicine procedure complication',
				'Neurosurgical procedure complication',
				'Ophthalmic procedure complication',
				'Orthopaedic procedure complication',
				'Soft tissue procedure complication',
				'Transfusion complication',
				'Endoscopy complication'
			]
		},
		{
			categoryName: 'Cardiopulmonary Arrest & Death',
			subCategories: [
				'CPA - DNR',
				'CPA - resuscitation unsuccessful',
				'CPA - resuscitation successful',
				'Death - unknown cause'
			]
		},
		{
			categoryName: 'Adverse Events',
			subCategories: [
				'Adverse reaction',
				'Adverse reaction to medication - oral',
				'Adverse reaction to medication - injectable',
				'Adverse reaction to medication - topical',
				'Iatrogenic complication',
				'Patient condition'
			]
		},
		{
			categoryName: 'Misc',
			subCategories: [
				'Incident',
				'Insufficient treatment',
				'Query',
				'Post mortem report'
			]
		}
	];

	constructor() { }

	getCategoryNames(): string[] {
		return this.categories.map(category => category.categoryName);
	}

	getSubCategories(categoryName: string): string[] {
		const category = this.categories.find(
			item => item.categoryName === categoryName
		);

		return category?.subCategories ?? [];
	}

	getCategories(): ComplicationCategory[] {
		return this.categories;
	}
}